<template>
  <div class="layout-container flex h-full grow flex-col">
    <!-- Main Content -->
    <main class="px-4 sm:px-6 md:px-16 lg:px-24 xl:px-40 flex flex-1 flex-col py-8 md:py-10">
      <div class="layout-content-container flex flex-col max-w-5xl w-full mx-auto flex-1">
        <!-- Header with Search and Sort -->
        <div class="flex flex-wrap justify-between items-center gap-4 mb-6">
          <h1 class="text-slate-900 dark:text-slate-100 text-3xl font-semibold leading-tight">Listado de Estimaciones</h1>
            <div class="flex items-center gap-2">
            <div class="relative">
              <span class="material-icons-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-xl pointer-events-none">search</span>
              <input 
              type="text"
              v-model="searchQuery"
              class="w-full py-3 pl-10 pr-4 border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:focus:ring-orange-400 dark:focus:border-orange-400 transition-all"
              placeholder="Buscar estimaciones..."
              />
            </div>
            </div>
        </div>

        <!-- Table Section -->
        <div class="bg-white dark:bg-slate-800 shadow-sm rounded-lg overflow-hidden @container">
          <table class="w-full">
            <thead class="bg-[#44403A] dark:bg-slate-900 border-b border-[#44403A] dark:border-slate-700">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-300 dark:text-slate-400 w-20">
                  <div class="flex items-center gap-1 cursor-pointer group" @click="sortBy('id')">
                    ID
                    <span class="material-icons-round text-sm text-slate-400 group-hover:text-slate-300 dark:group-hover:text-slate-200">
                      {{ sortField === 'id' ? (sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                    </span>
                    </div>
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-300 dark:text-slate-400 w-1/4">
                  <div class="flex items-center gap-1 cursor-pointer group" @click="sortBy('createdAt')">
                    FECHA
                    <span class="material-icons-round text-sm text-slate-400 group-hover:text-slate-300 dark:group-hover:text-slate-200">
                      {{ sortField === 'createdAt' ? (sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                    </span>
                  </div>
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-300 dark:text-slate-400 w-1/2">
                  CLIENTE / PROYECTO
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-300 dark:text-slate-400 w-1/4">
                  <div class="flex items-center gap-1 cursor-pointer group" @click="sortBy('totalHours')">
                    HORAS TOTALES
                    <span class="material-icons-round text-sm text-slate-400 group-hover:text-slate-300 dark:group-hover:text-slate-200">
                      {{ sortField === 'totalHours' ? (sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                    </span>
                  </div>
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-300 dark:text-slate-400"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
              <tr v-for="budget in filteredBudgets" 
                  :key="budget.id" 
                  class="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors group">
                <td class="px-4 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-slate-400">
                  #{{ budget.id }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{ formatDate(budget.createdAt) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                  <div class="font-medium text-slate-800 dark:text-slate-200">
                    {{ budget.type === 'team' ? budget.clientName : budget.clientName }}
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400 space-y-1">
                    <div class="flex items-center gap-2">
                      <span v-if="budget.type === 'team'" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                        Estimación por Equipo
                      </span>
                      <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                        Estimación por Items
                      </span>
                      <span v-if="budget.type === 'team' && budget.projectId" class="text-slate-400 dark:text-slate-500">
                        Proyecto: {{ budget.projectId }}
                      </span>
                    </div>
                    <div class="text-slate-600 dark:text-slate-400">
                      Estimado por: {{ budget.type === 'team' ? (budget.projectName || 'No especificado') : (budget.ownerName || 'No especificado') }}
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <div class="flex flex-col">
                    <div class="font-medium text-slate-700 dark:text-slate-300 text-lg">{{ budget.type === 'regular' ? Math.round(calculateTotalHours(budget.items) * 10) / 10 : Math.round((budget.totalHours || 0) * 10) / 10 }}</div>
                    <div class="font-normal text-slate-500 dark:text-slate-400 text-xs">horas totales</div>
                    <div v-if="budget.type === 'regular' && (calculateAutoSupervisionHoursFromItems(budget.items) > 0 || calculateSupervisionOnlyHours(budget.items) > 0)" class="font-normal text-slate-400 dark:text-slate-500 text-xs mt-1">
                      ({{ Math.round(calculateBaseHours(budget.items) * 10) / 10 }} + {{ Math.round((calculateAutoSupervisionHoursFromItems(budget.items) + calculateSupervisionOnlyHours(budget.items)) * 10) / 10 }} supervisión)
                    </div>
                    <div v-if="budget.type === 'team'" class="font-normal text-slate-400 dark:text-slate-500 text-xs mt-1">
                      <span class="material-icons-round text-xs align-middle mr-1">people</span>
                      {{ getTotalTeamMembers(budget.teamMembers) }} miembros
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end gap-2">
                    <button 
                      @click="viewBudget(budget.id, budget.type)"
                      class="text-slate-600 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 font-medium hover:underline focus:outline-none flex items-center gap-1 transition-colors"
                    >
                      <span class="material-icons-round text-sm">visibility</span>
                      Ver
                    </button>
                    <button 
                      @click="loadBudget(budget.id, budget.type)"
                      class="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium hover:underline focus:outline-none flex items-center gap-1 transition-colors"
                    >
                      <span class="material-icons-round text-sm">edit</span>
                      Editar
                    </button>
                    <button 
                      @click="confirmDelete(budget)"
                      class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium hover:underline focus:outline-none flex items-center gap-1 transition-colors"
                    >
                      <span class="material-icons-round text-sm">delete</span>
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Enhanced Pagination -->
          <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <p class="text-sm text-slate-600 dark:text-slate-400">
              Mostrando {{ startIndex + 1 }} a {{ endIndex }} de {{ totalFilteredBudgets }} estimaciones
            </p>
            <div class="flex items-center gap-2">
              <!-- Page numbers -->
              <div class="hidden sm:flex items-center gap-1">
                <button 
                  v-for="page in totalPages" 
                  :key="page"
                  @click="changePage(page)"
                  :class="[
                    'px-3 py-1 rounded-md text-sm font-medium',
                    currentPage === page 
                      ? 'bg-orange-600 dark:bg-orange-700 text-white' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  ]"
                >
                  {{ page }}
                </button>
              </div>
              
              <!-- Navigation arrows -->
              <div class="flex gap-1">
                <button 
                  @click="prevPage"
                  :disabled="currentPage === 1"
                  class="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="material-icons-round text-xl">chevron_left</span>
                </button>
                <button 
                  @click="nextPage"
                  :disabled="currentPage === totalPages"
                  class="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="material-icons-round text-xl">chevron_right</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Replace footer with component -->
    <footer-section />
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import Swal from 'sweetalert2';
import FooterSection from '../components/ui/Footer.vue';
import { calculateTotalHours, calculateBaseHours, calculateSupervisionHours, hasSupervision, calculateAutoSupervisionHoursFromItems, calculateSupervisionOnlyHours } from '@/utils/budgetCalculations';
import { useAuth } from '@/composables/useAuth'; // Import useAuth itself

export default {
  components: {
    FooterSection
  },
  setup() {
    const toast = useToast();
    const { getAuthHeader } = useAuth(); // Call useAuth() to get getAuthHeader
    // Authentication debugging removed for security
    
    return { 
      toast,
      calculateBaseHours,
      calculateTotalHours,
      calculateSupervisionHours,
      hasSupervision,
      calculateAutoSupervisionHoursFromItems,
      calculateSupervisionOnlyHours,
      getAuthHeader, // Expose getAuthHeader obtained from useAuth()
    };
  },
  data() {
    return {
      budgets: [],
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      sortField: 'id',
      sortDirection: 'desc'
    }
  },
  computed: {
    filteredBudgets() {
      // First filter and sort
      const filtered = this.budgets
        .filter(budget => {
          if (!this.searchQuery) return true;
          const searchLower = this.searchQuery.toLowerCase();
          
          if (budget.type === 'team') {
            return budget.projectName?.toLowerCase().includes(searchLower) ||
                   budget.clientName?.toLowerCase().includes(searchLower);
          } else {
            return budget.clientName?.toLowerCase().includes(searchLower) ||
                   budget.ownerName?.toLowerCase().includes(searchLower);
          }
        })
        .sort((a, b) => {
          const modifier = this.sortDirection === 'asc' ? 1 : -1;
          
          if (this.sortField === 'id') {
            return modifier * (a.id - b.id);
          }
          
          if (this.sortField === 'createdAt') {
            return modifier * (new Date(a[this.sortField]) - new Date(b[this.sortField]));
          }
          
          if (this.sortField === 'totalHours') {
            const hoursA = a.type === 'regular' ? Math.round(this.calculateTotalHours(a.items) * 10) / 10 : Math.round((a.totalHours || 0) * 10) / 10;
            const hoursB = b.type === 'regular' ? Math.round(this.calculateTotalHours(b.items) * 10) / 10 : Math.round((b.totalHours || 0) * 10) / 10;
            return modifier * (hoursA - hoursB);
          }
          
          // Handle other sort fields if needed
          return 0;
        });

      // Then paginate
      return filtered.slice(this.startIndex, this.endIndex);
    },
    totalFilteredBudgets() {
      // Count of filtered budgets before pagination
      return this.budgets.filter(budget => {
        if (!this.searchQuery) return true;
        const searchLower = this.searchQuery.toLowerCase();
        
        if (budget.type === 'team') {
          return budget.projectName?.toLowerCase().includes(searchLower) ||
                 budget.clientName?.toLowerCase().includes(searchLower);
        } else {
          return budget.clientName?.toLowerCase().includes(searchLower) ||
                 budget.ownerName?.toLowerCase().includes(searchLower);
        }
      }).length;
    },
    totalPages() {
      return Math.ceil(this.totalFilteredBudgets / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalFilteredBudgets);
    }
  },
  methods: {
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    getTotalTeamMembers(teamMembers) {
      try {
        // teamMembers is already an array from the backend
        if (Array.isArray(teamMembers)) {
          return teamMembers.length;
        }
        // Fallback for legacy data that might still be JSON string
        const parsed = JSON.parse(teamMembers);
        return parsed.length;
      } catch (error) {
        console.error('Error counting team members:', error);
        return 0;
      }
    },
    getTotalTeamHours(teamMembers) {
      try {
        // teamMembers is already an array from the backend
        let members = teamMembers;
        if (!Array.isArray(teamMembers)) {
          // Fallback for legacy data that might still be JSON string
          members = JSON.parse(teamMembers);
        }
        const total = members.reduce((sum, member) => {
          return sum + (parseFloat(member.estimatedHours) || 0);
        }, 0);
        return Math.round(total * 10) / 10;
      } catch (error) {
        console.error('Error calculating team hours:', error);
        return 0;
      }
    },
    // Add this method to handle page changes
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    prevPage() {
      this.changePage(this.currentPage - 1);
    },
    nextPage() {
      this.changePage(this.currentPage + 1);
    },
    toggleSort() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },
    resetPagination() {
      this.currentPage = 1;
    },
    async confirmDelete(budget) {
      if (!budget || !budget.id) {
        this.toast.error("Error: Presupuesto inválido");
        return;
      }

      const budgetName = budget.clientName;
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar la estimación para "${budgetName}"?`, // Removed unnecessary escapes
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6B7280',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        try {
          const authHeaders = this.getAuthHeader(); 
          const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${apiUrl}/api/budgets/${budget.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders
            }
          });

          if (!response.ok) {
            let errorMsg = 'Error al eliminar la estimación';
            const responseText = await response.text();
            if (response.status === 401) {
              errorMsg = 'No autorizado para eliminar esta estimación. Por favor, inicie sesión de nuevo.';
            } else if (response.status === 403) {
              errorMsg = 'No tiene permisos para eliminar esta estimación.';
            } else {
              errorMsg = `Error al eliminar: ${response.status} ${response.statusText}`;
            }
            this.toast.error(errorMsg);
            console.error('Error deleting budget - API response not OK:', response.status, responseText);
            return; 
          }

          this.budgets = this.budgets.filter(b => b.id !== budget.id);
          this.toast.success('Estimación eliminada exitosamente');
        } catch (error) {
          console.error('Error deleting budget during request:', error);
          this.toast.error('Error al eliminar la estimación durante la solicitud.');
        }
      }
    },

    // Add this new method to handle budget removal from the list
    updateBudgetsList(deletedBudgetId) {
      this.budgets = this.budgets.filter(budget => budget.id !== deletedBudgetId);
    },
    loadBudget(id, budgetType) {
      if (budgetType === 'team') {
        // Navigate to team assignment estimator with the budget ID
        this.$router.push({
          path: '/team-assignment',
          query: { budgetId: id }
        });
      } else {
        // Navigate to the calculator view with the budget ID
        this.$router.push({
          path: '/',
          query: { budgetId: id }
        });
      }
    },
    viewBudget(id, budgetType) {
      if (budgetType === 'team') {
        this.$router.push({
          name: 'team-budget-view',
          params: { id }
        });
      } else {
        this.$router.push({
          name: 'budget-view',
          params: { id }
        });
      }
    },
  },
  watch: {
    // Add watchers to reset pagination when filters change
    searchQuery() {
      this.resetPagination();
    },
    sortField() {
      this.resetPagination();
    },
    sortDirection() {
      this.resetPagination();
    }
  },
  async mounted() {
    try {
      const headers = this.getAuthHeader(); // Use this.getAuthHeader()
      const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/budgets`, { headers });
      if (!response.ok) {
        // Log the error response for more details
        const errorData = await response.text(); // Use .text() in case it's not JSON
        console.error('Error fetching budgets - API response not OK:', response.status, errorData);
        this.toast.error(`Error al cargar los presupuestos: ${response.status}`);
        this.budgets = []; // Ensure budgets is an empty array on error
        return;
      }
      
      const budgetsData = await response.json();
      
      // Ensure that budgetsData is an array before assigning
      if (Array.isArray(budgetsData)) {
        this.budgets = budgetsData;
      } else {
        console.error('Error fetching budgets: API did not return an array.', budgetsData);
        this.toast.error('Error al cargar los presupuestos: Formato de datos incorrecto.');
        this.budgets = []; // Default to an empty array if data is not an array
      }
    } catch (error) {
      console.error('Error fetching budgets:', error);
      this.toast.error('Error al cargar los presupuestos');
      this.budgets = []; // Ensure budgets is an empty array on any other error
    }
  }
}
</script>
